import { applyPatch, Operation } from "rfc6902";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import * as SharedChatResponse from "../../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as MessageShared from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import * as SharedMessageTypes from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import { ErrorContent as ErrorContentShared } from "../../../darvin-shared/schema/zod/agent/message-types.schema";
import {
  ProjectError,
  useProjectStore,
} from "../../core_stores/useProjectStore";
import { ImageService } from "../../services/imageService";
import {
  AssistantMessageAnimationStatus,
  ProjectChatAssistantMessage,
  ProjectChatAssistantMsgType,
  ProjectChatElement,
  ProjectChatException,
  ProjectChatTemporaryUserMessage,
  ProjectChatUserMessage,
} from "./chatModels";

const PAGINATION_LIMIT = 20;

interface ChatMessagesActions {
  startListening: () => void;
  addTemporaryMessage: (
    message: string,
    attachedImg: File | null
  ) => Promise<void>;
  removeTemporaryMessages: () => void;
  onAssistantMessageAnimationFinished: () => void;
  addPreviewError: (error: string) => void;
  dispose: () => void;
  setShouldAutoScroll: (value: boolean) => void;
  increaseNumberOfVisisbleMessages: () => void;
}

interface ChatMessagesState {
  chatElements: ProjectChatElement[];
  exception: ProjectChatException | null;
  shouldAutoScroll: boolean;
  numberOfVisibleMessages: number;

  actions: ChatMessagesActions;
}

export const useChatMessagesStore = create<ChatMessagesState>()(
  subscribeWithSelector((set, get) => {
    let unsubscribeHistory: (() => void) | null = null;
    let unsubscribeLatestMsg: (() => void) | null = null;
    let unsubscribeError: (() => void) | null = null;
    const processedMessages: Set<SharedChatResponse.MessageBody> = new Set();

    function hasMessageBeenProcessed(
      message: SharedChatResponse.MessageBody
    ): boolean {
      for (const processedMessage of processedMessages) {
        if (
          get().chatElements.some(
            (chatEl) => chatEl.id === message.messageId
          ) &&
          JSON.stringify(message) === JSON.stringify(processedMessage)
        ) {
          return true;
        }
      }
      return false;
    }

    function handleNotEnoughTokens() {
      set((state) => {
        const isAnyMessageAnimating = state.chatElements.some(
          (el) =>
            el instanceof ProjectChatAssistantMessage &&
            el.animationStatus === AssistantMessageAnimationStatus.ANIMATING
        );
        const updatedChatElements = [
          ...(state.chatElements ?? []),
          new ProjectChatAssistantMessage(
            `not-enough-tokens-${Date.now()}`,
            Date.now(),
            isAnyMessageAnimating
              ? AssistantMessageAnimationStatus.WAITING
              : AssistantMessageAnimationStatus.ANIMATING,
            0,
            true,
            { type: "notEnoughTokens" },
            "error"
          ),
        ];
        return {
          chatElements: updatedChatElements,
          numberOfVisibleMessages: get().numberOfVisibleMessages + 1,
        };
      });
    }

    function historyUpdated(history: SharedChatResponse.MessageBody[]) {
      const chatElementsExistingInHistory =
        removeChatElementsNotIncludedInHistory(
          history,
          get().chatElements ?? []
        );
      let updatedChatElements = [...chatElementsExistingInHistory];

      if (history.length === 0) {
        set({
          chatElements: updatedChatElements,
          numberOfVisibleMessages: updatedChatElements.length,
        });
        return;
      }

      for (const message of history) {
        if (hasMessageBeenProcessed(message)) {
          continue;
        }
        updatedChatElements = processMessage(
          message,
          updatedChatElements,
          false
        );
      }

      updatedChatElements = sortMessages(updatedChatElements);
      updatedChatElements =
        updateAnimationStatusOfMessages(updatedChatElements);

      let newNumOfVisibleMsgs = get().numberOfVisibleMessages;
      const numOfAllChatElements = updatedChatElements.length;
      const currentNumOfAllChatElements = get().chatElements.length;
      if (newNumOfVisibleMsgs === 0) {
        newNumOfVisibleMsgs =
          numOfAllChatElements <= PAGINATION_LIMIT
            ? numOfAllChatElements
            : PAGINATION_LIMIT;
      } else if (numOfAllChatElements < newNumOfVisibleMsgs) {
        newNumOfVisibleMsgs = numOfAllChatElements;
      } else if (numOfAllChatElements < currentNumOfAllChatElements) {
        const diff = currentNumOfAllChatElements - numOfAllChatElements;
        newNumOfVisibleMsgs = get().numberOfVisibleMessages - diff;
      }

      set({
        chatElements: updatedChatElements,
        numberOfVisibleMessages: newNumOfVisibleMsgs,
      });
    }

    function removeChatElementsNotIncludedInHistory(
      history: SharedChatResponse.MessageBody[],
      chatElements: ProjectChatElement[]
    ): ProjectChatElement[] {
      return [...chatElements].filter(
        (el) =>
          el instanceof ProjectChatTemporaryUserMessage ||
          history.some((msg) => msg.messageId === el.id)
      );
    }

    function messageReceived(msg: SharedChatResponse.MessageBody) {
      if (hasMessageBeenProcessed(msg)) {
        return;
      }

      let updatedChatElements = processMessage(
        msg,
        get().chatElements ?? [],
        true
      );
      updatedChatElements = sortMessages(updatedChatElements);
      updatedChatElements =
        updateAnimationStatusOfMessages(updatedChatElements);
      if (updatedChatElements.length > 1) {
        const lastElement = updatedChatElements[updatedChatElements.length - 1];
        const secondToLastElement =
          updatedChatElements[updatedChatElements.length - 2];
        if (
          lastElement instanceof ProjectChatAssistantMessage &&
          secondToLastElement instanceof ProjectChatAssistantMessage &&
          lastElement.type === "error" &&
          secondToLastElement.type === "error" &&
          (lastElement.value as ErrorContentShared).type ===
            SharedMessageTypes.ErrorTypeEnum.enum.technical &&
          (secondToLastElement.value as ErrorContentShared).type ===
            SharedMessageTypes.ErrorTypeEnum.enum.technical &&
          (secondToLastElement.value as ErrorContentShared).message ===
            (lastElement.value as ErrorContentShared).message
        ) {
          // There are two duplicated technical errors, so remove only second to last one
          updatedChatElements.splice(updatedChatElements.length - 2, 1);
        }
      }

      let updatedNumberOfVisibleMessages = get().numberOfVisibleMessages;
      if (updatedChatElements.length > get().chatElements.length) {
        updatedNumberOfVisibleMessages++;
      }
      set({
        chatElements: updatedChatElements,
        numberOfVisibleMessages: updatedNumberOfVisibleMessages,
      });
    }

    function processMessage(
      msg: SharedChatResponse.MessageBody,
      chatElements: ProjectChatElement[],
      canBeAnimated: boolean
    ): ProjectChatElement[] {
      const updatedChatElements = [...chatElements];

      // Remove temporary user message if it exists
      if (msg.type === SharedChatResponse.MessageTypeEnum.enum.userMessage) {
        const tempMsgIndex = updatedChatElements.findIndex(
          (el) => el instanceof ProjectChatTemporaryUserMessage
        );
        if (tempMsgIndex !== -1) {
          updatedChatElements.splice(tempMsgIndex, 1);
        }
      }

      const chatElement: ProjectChatElement | undefined =
        mapMessageToChatElement(msg, updatedChatElements, canBeAnimated);
      if (chatElement !== undefined) {
        const existingElementIndex = updatedChatElements.findIndex(
          (el) => el.id === chatElement.id
        );

        if (existingElementIndex === -1) {
          if (updatedChatElements.length > 0) {
            const lastElement =
              updatedChatElements[updatedChatElements.length - 1];
            if (
              lastElement instanceof ProjectChatAssistantMessage &&
              !lastElement.fullyGenerated
            ) {
              lastElement.fullyGenerated = true;
            }
          }
          updatedChatElements.push(chatElement);
        } else {
          updatedChatElements[existingElementIndex] = chatElement;
        }
      }

      processedMessages.add(msg);
      return updatedChatElements;
    }

    function sortMessages(updatedChatElements: ProjectChatElement[]) {
      return updatedChatElements.sort((a, b) => a.timestamp - b.timestamp);
    }

    function updateAnimationStatusOfMessages(
      chatElements: ProjectChatElement[]
    ): ProjectChatElement[] {
      const updatedChatElements = [...chatElements];
      const isNoAssistantElementAnimating =
        updatedChatElements.findIndex(
          (el) =>
            el instanceof ProjectChatAssistantMessage &&
            el.animationStatus === AssistantMessageAnimationStatus.ANIMATING
        ) === -1;
      if (isNoAssistantElementAnimating) {
        const indexOfFirstWaitingAssistantElement =
          updatedChatElements.findIndex(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.WAITING
          );
        if (indexOfFirstWaitingAssistantElement > -1) {
          (
            updatedChatElements[
              indexOfFirstWaitingAssistantElement
            ] as ProjectChatAssistantMessage
          ).animationStatus = AssistantMessageAnimationStatus.ANIMATING;
        }
      }
      return updatedChatElements;
    }

    function mapMessageToChatElement(
      message: SharedChatResponse.MessageBody,
      chatElements: ProjectChatElement[],
      canBeAnimated: boolean
    ): ProjectChatElement | undefined {
      if (
        message.type === SharedChatResponse.MessageTypeEnum.enum.userMessage
      ) {
        return new ProjectChatUserMessage(
          message.messageId,
          message.timestamp,
          message.content,
          message.checkpointId!
        );
      } else {
        let msgType: ProjectChatAssistantMsgType;
        switch (message.type) {
          case SharedChatResponse.MessageTypeEnum.enum.general:
            msgType = "general";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.tools:
            msgType = "tools";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.projectConfig:
            msgType = "projectConfig";
            break;
          case SharedChatResponse.MessageTypeEnum.enum.error:
            msgType = "error";
            break;
          default:
            throw new Error(`Unknown message type: ${message.type}`);
        }

        const existingMsg = chatElements.find(
          (el) => el.id === message.messageId
        ) as ProjectChatAssistantMessage | undefined;
        const content = message.stream
          ? applyDeltas(
              message.type,
              existingMsg?.value,
              message.chunk!.delta as Operation[]
            )
          : message.content;

        if (
          Object.keys(content).length === 0 ||
          ("files" in content && Object.keys(content.files).length === 0)
        ) {
          return undefined;
        }

        let animationStatus = AssistantMessageAnimationStatus.WAITING;
        if (existingMsg !== undefined) {
          animationStatus = existingMsg.animationStatus;
        } else if (
          (chatElements.length > 0 &&
            message.timestamp <
              chatElements[chatElements.length - 1].timestamp) ||
          !canBeAnimated
        ) {
          animationStatus = AssistantMessageAnimationStatus.DISPLAYED;
        } else if (canBeAnimated) {
          animationStatus = AssistantMessageAnimationStatus.WAITING;
        }

        return new ProjectChatAssistantMessage(
          message.messageId,
          message.timestamp,
          animationStatus,
          existingMsg?.visibleWords ?? 0,
          message.stream ? message.chunk!.done : true,
          content,
          msgType
        );
      }
    }

    function applyDeltas(
      type: SharedChatResponse.MessageType,
      existingValue: any,
      delta: Operation[]
    ): SharedMessageTypes.GeneralAssistantMessage | SharedMessageTypes.Tools {
      const value: any =
        type === SharedChatResponse.MessageTypeEnum.enum.tools
          ? existingValue ?? {
              toolUses: {},
            }
          : existingValue ?? {};

      applyPatch(value, delta as Operation[]);

      return value;
    }

    function isMessageToShowOnChat(
      message: SharedChatResponse.MessageBody
    ): boolean {
      return (
        message.type === SharedChatResponse.MessageTypeEnum.enum.userMessage ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.general ||
        message.type ===
          SharedChatResponse.MessageTypeEnum.enum.projectConfig ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.tools ||
        message.type === SharedChatResponse.MessageTypeEnum.enum.error
      );
    }

    return {
      chatElements: [],
      exception: null,
      shouldAutoScroll: true,
      numberOfVisibleMessages: 0,

      actions: {
        addTemporaryMessage: async (
          message: string,
          attachedImg: File | null
        ) => {
          set({ shouldAutoScroll: true });
          const base64 = attachedImg
            ? await ImageService.convertImageToBase64(attachedImg)
            : null;
          const tempMessage = new ProjectChatTemporaryUserMessage(
            `temp-${Date.now()}`,
            Date.now(),
            message,
            base64
              ? MessageShared.UserMessageAttachmentSchema.parse({
                  content: base64,
                  mimeType: attachedImg?.type ?? undefined,
                  filename: attachedImg?.name ?? undefined,
                })
              : null
          );
          set((state) => {
            // Filter out any existing temporary messages
            const filteredChatElements = (state.chatElements ?? []).filter(
              (el) => !(el instanceof ProjectChatTemporaryUserMessage)
            );
            const updatedChatElements = [...filteredChatElements, tempMessage];
            return {
              chatElements: updatedChatElements,
              shouldAutoScroll: true,
              numberOfVisibleMessages: get().numberOfVisibleMessages + 1,
            };
          });
        },

        removeTemporaryMessages: () => {
          set((state) => {
            const filteredChatElements = (state.chatElements ?? []).filter(
              (el) => !(el instanceof ProjectChatTemporaryUserMessage)
            );
            return { chatElements: filteredChatElements };
          });
        },

        startListening: () => {
          if (unsubscribeHistory && unsubscribeLatestMsg) {
            return;
          }

          unsubscribeHistory = useProjectStore.subscribe(
            (state) => state.history,
            (history) => {
              if (history) {
                const messagesToShow = history.filter(isMessageToShowOnChat);
                historyUpdated(messagesToShow);
              }
            }
          );
          unsubscribeLatestMsg = useProjectStore.subscribe(
            (state) => state.latestMsg,
            (latestMsg) => {
              if (latestMsg && isMessageToShowOnChat(latestMsg)) {
                messageReceived(latestMsg);
              }
            }
          );
          unsubscribeError = useProjectStore.subscribe(
            (state) => state.error,
            (error) => {
              if (error === ProjectError.NOT_ENOUGH_TOKENS) {
                handleNotEnoughTokens();
                return;
              }
            }
          );
        },

        dispose: () => {
          if (unsubscribeHistory) {
            unsubscribeHistory();
          }
          unsubscribeHistory = null;
          if (unsubscribeLatestMsg) {
            unsubscribeLatestMsg();
          }
          unsubscribeLatestMsg = null;
          if (unsubscribeError) {
            unsubscribeError();
          }
          unsubscribeError = null;

          processedMessages.clear();
          set({
            chatElements: [],
            exception: null,
            numberOfVisibleMessages: 0,
          });
        },

        onAssistantMessageAnimationFinished: () => {
          const updatedChatElements = [...get().chatElements];
          const animatingMessageIndex = updatedChatElements.findIndex(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.ANIMATING
          );
          if (animatingMessageIndex !== -1) {
            const animatingMessage = updatedChatElements[
              animatingMessageIndex
            ] as ProjectChatAssistantMessage;
            if (animatingMessage.fullyGenerated) {
              animatingMessage.animationStatus =
                AssistantMessageAnimationStatus.DISPLAYED;
              if (animatingMessageIndex + 1 < updatedChatElements.length) {
                (
                  updatedChatElements[
                    animatingMessageIndex + 1
                  ] as ProjectChatAssistantMessage
                ).animationStatus = AssistantMessageAnimationStatus.ANIMATING;
              }
            }
          }
          set({
            chatElements: updatedChatElements,
          });
        },

        addPreviewError: (error: string) => {
          const doesPreviewErrorAlreadyExist = get().chatElements.some(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.id === `preview-error`
          );
          if (doesPreviewErrorAlreadyExist) {
            return;
          }
          const isAnyMsgAnimating = get().chatElements.some(
            (el) =>
              el instanceof ProjectChatAssistantMessage &&
              el.animationStatus === AssistantMessageAnimationStatus.ANIMATING
          );
          set({
            chatElements: [
              ...(get().chatElements ?? []),
              new ProjectChatAssistantMessage(
                `preview-error`,
                Date.now(),
                isAnyMsgAnimating
                  ? AssistantMessageAnimationStatus.WAITING
                  : AssistantMessageAnimationStatus.ANIMATING,
                0,
                true,
                {
                  type: "runtime",
                  message: error,
                },
                "error"
              ),
            ],
          });
        },

        setShouldAutoScroll: (value: boolean) => {
          set({ shouldAutoScroll: value });
        },

        increaseNumberOfVisisbleMessages: () => {
          if (get().numberOfVisibleMessages < get().chatElements.length) {
            set((state) => ({
              numberOfVisibleMessages:
                state.numberOfVisibleMessages + PAGINATION_LIMIT,
            }));
          }
        },
      },
    };
  })
);

export const useChatElements = () =>
  useChatMessagesStore((state) =>
    state.chatElements.slice(-state.numberOfVisibleMessages)
  );

export const useChatException = () =>
  useChatMessagesStore((state) => state.exception);

export const useShouldAutoScroll = () =>
  useChatMessagesStore((state) => state.shouldAutoScroll);

export const useHasMoreChatElements = () =>
  useChatMessagesStore(
    (state) => state.numberOfVisibleMessages < state.chatElements.length
  );

export const useChatMessagesActions = () =>
  useChatMessagesStore((state) => state.actions);
